import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, OnDestroy, resource, ResourceRef } from '@angular/core';
import { select } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { OrganizationLimits, OrganizationUsage } from '@sona/shared-model-common';

import { SonaAuthFacade } from './auth.facade';


/**
 * A facade for the general authentication
 */
@Injectable({
    providedIn: 'root'
})
export class SonaUsageFacade implements OnDestroy {

    private readonly _httpClient = inject(HttpClient);

    protected readonly _isAuthenticated = select(SonaAuthFacade.isAuthenticated);
    private readonly _usageResource: ResourceRef<OrganizationUsage | null> = resource({
        request: () => ({ isAuthenticated: this._isAuthenticated() }),
        loader: ({ request }) => request.isAuthenticated ? firstValueFrom(this._httpClient.get<OrganizationUsage>('/api/organizations/usage')) : Promise.resolve(null)
    });
    private readonly _limitsResource: ResourceRef<OrganizationLimits | null> = resource({
        request: () => ({ isAuthenticated: this._isAuthenticated() }),
        loader: ({ request }) => request.isAuthenticated ? firstValueFrom(this._httpClient.get<OrganizationLimits>('/api/organizations/limits')) : Promise.resolve(null)
    });
    readonly usageResource = this._usageResource.asReadonly();
    readonly limitsResource = this._limitsResource.asReadonly();
    readonly usage = this._usageResource.value.asReadonly();
    readonly limits = this._limitsResource.value.asReadonly();
    readonly subscriptionPlan = computed(() => {
        const usage = this.usage();
        return usage?.subscription.plan ?? null;
    });
    readonly isLoading = computed(() => this._usageResource.isLoading() || this._limitsResource.isLoading());
    readonly hasValue = computed(() => this._usageResource.hasValue() && this._limitsResource.hasValue());

    readonly isUsageLimitReached = computed(() => {
        if (!this.hasValue()) {
            return undefined;
        }

        const limits = this.limits();
        const usage = this.usage();

        if (limits == null || usage == null) {
            return undefined;
        }

        return limits.credits === -1 ? false : (usage.creditsUsed ?? 0) >= limits.credits;
    });
    readonly isTrialing = computed(() => {
        if (this.hasValue()) {
            return undefined;
        }

        const usage = this.usage();

        if (!usage) {
            return undefined;
        }

        return usage.subscription.status === 'trialing';
    });

    readonly usageReloadInterval = setInterval(() => {
        if (!this._isAuthenticated()) {
            return;
        }

        this._usageResource.reload();
    }, 60000);

    ngOnDestroy() {
        clearInterval(this.usageReloadInterval);
    }

    updateUsage() {
        this._usageResource.reload();
    }
}
